import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";

/**
 * Reads employees from the 'employees' table (Staff Management)
 * and shapes them for use in the CRM as team members.
 */
export function useTeamMembers() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        const { data, error } = await supabase
          .from("employees")
          .select("id, full_name, job_title, department, email, avatar_url, status")
          .order("full_name", { ascending: true });

        if (!active) return;
        if (error) { console.warn("useTeamMembers:", error.message); }

        const shaped = (data || []).map((e, i) => {
          const COLORS = ["#EC407A","#26A69A","#F57C00","#5C6BC0","#3761E9","#00897B","#AB47BC"];
          const name = e.full_name || e.email || "Unknown";
          return {
            id:       e.id,
            name,
            email:    e.email    || "",
            position: e.job_title || e.department || "",
            color:    COLORS[i % COLORS.length],
            initials: name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2),
            status:   e.status === "Active" ? "online" : "offline",
            avatar:   e.avatar_url || null,
          };
        });

        setTeamMembers(shaped);
      } catch (err) {
        console.warn("useTeamMembers error:", err.message);
      } finally {
        if (active) setLoading(false);
      }
    }

    load();

    // Realtime: refresh when employees table changes
    const ch = supabase
      .channel("employees_crm")
      .on("postgres_changes", { event: "*", schema: "public", table: "employees" }, load)
      .subscribe();

    return () => { active = false; supabase.removeChannel(ch); };
  }, []);

  return { teamMembers, loading };
}

// ── NEW: Hook to get current logged-in user + employee details ──
export function useCurrentUser() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    let active = true;
    async function fetchUser() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !active) return;

        // Fetch employee details
        const { data: emp } = await supabase
          .from("employees")
          .select("*")
          .eq("email", user.email)
          .single();
        
        if (active) {
          setCurrentUser({
            id:              user.id,
            email:           user.email,
            name:            emp?.full_name || user.user_metadata?.full_name || user.email,
            avatar:          emp?.avatar_url || user.user_metadata?.avatar_url,
            employee_number: emp?.employee_number,
            job_title:       emp?.job_title,
          });
        }
      } catch (err) {
        console.warn("useCurrentUser error:", err.message);
      } finally {
        if (active) setLoading(false);
      }
    }
    fetchUser();
    return () => { active = false; };
  }, []);

  return { currentUser, loading };
}
